import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, PhoneIcon, MessageCircleIcon, MapPinIcon } from 'lucide-react';
import { GoogleReviewBadge } from './GoogleReviewBadge';
import YouTube from 'react-youtube';
export const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  // Set up animation on load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => {
      clearTimeout(timer);
    };
  }, []);
  // YouTube video options
  const videoOptions = {
    playerVars: {
      autoplay: 1,
      controls: 0,
      rel: 0,
      showinfo: 0,
      mute: 1,
      loop: 1,
      modestbranding: 1,
      playsinline: 1,
      enablejsapi: 1,
      start: 15,
      origin: window.location.origin,
      playlist: '3oOz4xYL64I'
    }
  };
  const onVideoReady = (event: any) => {
    event.target.playVideo();
    setVideoLoaded(true);
  };
  return <section ref={sectionRef} className="relative h-[85vh] md:h-[80vh] lg:h-[75vh] w-full overflow-hidden" data-id="element-246" data-mp="2be6tc816zmrc">
      {/* Background video with overlay */}
      <div className="absolute inset-0 z-0" data-id="element-247" data-mp="2p7eo28kkkyd9">
        <div className="absolute inset-0 bg-black/70 sm:bg-black/60 z-10" data-id="element-248" data-mp="2ked3pl1lg6ma"></div>
        {/* Fallback image with proper aspect ratio */}
        <div className="absolute inset-0 w-full h-full" data-id="element-249" data-mp="2z1y95llxyhxd">
          <img src="https://images.unsplash.com/photo-1619771914272-e3c1ba17ba4d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="BKCustoms auto styling in Almere" className="w-full h-full object-cover object-center" style={{
          display: videoLoaded ? 'none' : 'block'
        }} data-id="element-250" data-mp="2u4y0r41md333" />
        </div>
        {/* YouTube Video Background */}
        <div className="absolute inset-0 w-full h-full overflow-hidden" data-id="element-251" data-mp="38tb8rn0knylv">
          <div className="relative w-full h-full pt-[56.25%]" data-id="element-252" data-mp="38v5u32fr9sqh">
            <YouTube videoId="3oOz4xYL64I" opts={videoOptions} onReady={onVideoReady} className="absolute inset-0 w-full h-full" iframeClassName="absolute top-1/2 left-1/2 md:w-[200%] md:h-[200%] w-[250%] h-[250%] -translate-x-1/2 -translate-y-1/2" data-id="element-253" data-mp="3dth4be9yq598" />
          </div>
        </div>
      </div>
      {/* Subtle pattern overlay - reduced opacity */}
      <div className="absolute inset-0 z-10 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre-v2.png')]" data-id="element-254" data-mp="kb8xh0r81cm"></div>
      {/* Content Container - Adjusted spacing */}
      <div className="relative z-20 h-full max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center" data-id="element-255" data-mp="aae67tews1gr">
        {/* Main content container - Improved vertical spacing */}
        <div className="max-w-3xl mx-auto text-center" data-id="element-256" data-mp="jdf81hwqr6em">
          {/* Main Heading - Adjusted size and spacing */}
          <div className={`mb-5 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`} data-id="element-257" data-mp="23vzhkz7715mj">
            <h1 className="font-bold text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight" data-id="element-258" data-mp="1yxo7cnczkt3s">
              Dé specialist in{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D6281F] to-[#FF6B00]" data-id="element-259" data-mp="1pvyfxhj3tu60">
                ramen blinderen & auto wrappen
              </span>{' '}
              <span className="block sm:inline mt-1" data-id="element-260" data-mp="2ij2ilfuq5wem">in Almere</span>
            </h1>
          </div>
          {/* Google Review Badge */}
          <div className={`mb-4 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100' : 'opacity-0'}`} data-id="element-261" data-mp="2dqrd7xh7tieo">
            <GoogleReviewBadge data-id="element-262" data-mp="368n3i4afv3qj" />
          </div>
          {/* Condensed SEO-optimized Description for mobile */}
          <p className={`text-white/80 text-sm max-w-xs mx-auto mb-4 font-normal transition-all duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0'} sm:hidden`} data-id="element-263" data-mp="31hwu8fyw6gqk">
            Premium auto styling: ramen tinten, car wrapping, lichten smoken en
            ontchromen in Almere.
          </p>
          {/* Full SEO-optimized Description for desktop */}
          <p className={`text-white/80 text-base md:text-lg max-w-2xl mx-auto mb-6 font-normal transition-all duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0'} hidden sm:block`} data-id="element-264" data-mp="2h0kb206j0lo6">
            Auto styling in Almere: ramen tinten voor privacy & UV-bescherming,
            car wrapping in matte & glanzende kleuren, lichten smoken voor een
            stoere look, en ontchromen voor moderne voertuigen. Vakkundige
            montage met 5-sterren beoordelingen in Flevoland.
          </p>
          {/* CTA Buttons - Moved up and improved spacing */}
          <div className={`flex flex-col sm:flex-row gap-4 sm:gap-5 justify-center transition-all duration-700 delay-300 mt-2 mb-4 ${isVisible ? 'opacity-100' : 'opacity-0'}`} data-id="element-265" data-mp="3sfz813yqjdja">
            {/* Primary CTA Button */}
            <Link to="/afspraak-maken" className="group relative overflow-hidden rounded-md" data-id="element-266" data-mp="1na3b4lcf8e0">
              <div className="bg-gradient-to-r from-[#FFD500] via-[#FF6B00] to-[#D6281F] py-3 px-6 animate-gradient-shift" data-id="element-267" data-mp="fhaund9ytt4c">
                <div className="flex items-center justify-center" data-id="element-268" data-mp="3ivx11meoltbk">
                  <span className="text-black font-bold tracking-wide uppercase mr-3 text-sm" data-id="element-269" data-mp="3iy9js1s9k7po">
                    Afspraak maken
                  </span>
                  <ArrowRightIcon size={18} className="text-black transition-transform duration-300 group-hover:translate-x-1" data-id="element-270" data-mp="3sve744xui982" />
                </div>
              </div>
            </Link>
            {/* Secondary Button - Hidden on mobile */}
            <Link to="/contact" className="group relative overflow-hidden py-3 px-6 border border-white/30 rounded-md bg-black/30 backdrop-blur-sm hover:bg-black/50 transition-all duration-300 hidden sm:block" data-id="element-271" data-mp="133zhmddu0brt">
              <span className="text-white font-bold tracking-wide uppercase text-sm" data-id="element-272" data-mp="1f96j8fh1f4vb">
                Vraag ons advies
              </span>
            </Link>
          </div>
        </div>
      </div>
      {/* Mobile Action Buttons - Fixed to bottom of screen on mobile only */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-t border-white/10 p-2 sm:hidden" data-id="element-273" data-mp="rjkihnl0ohl7">
        <div className="grid grid-cols-3 gap-1" data-id="element-274" data-mp="11fxbskcngtff">
          <a href="tel:+31627888306" className="flex flex-col items-center p-2 bg-[#0f0f0f]/80 rounded-md active:bg-[#D6281F]/20 transition-colors" data-id="element-275" data-mp="11f5gzbqvmzq7">
            <PhoneIcon size={20} className="text-[#D6281F] mb-1" data-id="element-276" data-mp="8q89m3o7enc8" />
            <span className="text-white text-xs font-medium" data-id="element-277" data-mp="dqcoxqddlc0v">Bel ons</span>
          </a>
          <Link to="/contact" className="flex flex-col items-center p-2 bg-[#0f0f0f]/80 rounded-md active:bg-[#D6281F]/20 transition-colors" data-id="element-278" data-mp="do01ixoum9gf">
            <MessageCircleIcon size={20} className="text-[#D6281F] mb-1" data-id="element-279" data-mp="18iejvhsmpmb" />
            <span className="text-white text-xs font-medium" data-id="element-280" data-mp="3scqmuzu9nj58">Contact</span>
          </Link>
          <a href="https://maps.google.com/?q=Microfoonstraat+36+1322+BN+Almere" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center p-2 bg-[#0f0f0f]/80 rounded-md active:bg-[#D6281F]/20 transition-colors" data-id="element-281" data-mp="3ndxa59mszw64">
            <MapPinIcon size={20} className="text-[#D6281F] mb-1" data-id="element-282" data-mp="39olpca47cuev" />
            <span className="text-white text-xs font-medium" data-id="element-283" data-mp="34oh466scnbib">Almere</span>
          </a>
        </div>
      </div>
      {/* Desktop Action Buttons - Only visible on screens larger than mobile */}
      <div className="hidden sm:block relative z-30 px-4 sm:px-6 lg:px-8 pb-6 sm:pb-0 sm:absolute sm:bottom-0 sm:left-0 sm:right-0 sm:mb-6 md:mb-8 lg:mb-10" data-id="element-284" data-mp="2vnrdtiluydbz">
        <div className="max-w-screen-xl mx-auto" data-id="element-285" data-mp="nfmbnisqkmb9">
          <div className="backdrop-blur-md bg-black/80 border border-white/10 rounded-lg sm:rounded-xl shadow-lg sm:shadow-2xl overflow-hidden transform transition-all duration-500 hover:shadow-[0_10px_30px_rgba(0,0,0,0.3)]" data-id="element-286" data-mp="sdw59oytbeur">
            <div className="grid grid-cols-3 sm:grid-cols-3 divide-x divide-white/10" data-id="element-287" data-mp="ieylt0g8ubim">
              <a href="tel:+31627888306" className="group flex flex-col sm:flex-row items-center justify-center sm:gap-3 py-3 sm:py-5 px-2 sm:px-4 hover:bg-white/5 transition-all duration-500" data-id="element-288" data-mp="4oblj3uu4dkf">
                <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#0f0f0f] to-[#1a1a1a] shadow-inner border border-white/5 mb-1 sm:mb-0" data-id="element-289" data-mp="3qxmkt1a12xqg">
                  <PhoneIcon size={16} className="text-[#D6281F] group-hover:scale-110 transition-transform duration-500" data-id="element-290" data-mp="3hvwu5xnz02tq" />
                </div>
                <div className="flex flex-col items-center sm:items-start" data-id="element-291" data-mp="338ocqf42j19b">
                  <span className="text-white/60 text-[10px] sm:text-xs tracking-wider uppercase block" data-id="element-292" data-mp="2yamufwegv5yw">
                    Bel ons
                  </span>
                  <span className="text-white font-medium tracking-wide text-xs sm:text-base" data-id="element-293" data-mp="37a9mvsp7x1ft">
                    06-278 883 06
                  </span>
                </div>
              </a>
              <Link to="/contact" className="group flex flex-col sm:flex-row items-center justify-center sm:gap-3 py-3 sm:py-5 px-2 sm:px-4 hover:bg-white/5 transition-all duration-500" data-id="element-294" data-mp="3h87e8p2djecn">
                <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#0f0f0f] to-[#1a1a1a] shadow-inner border border-white/5 mb-1 sm:mb-0" data-id="element-295" data-mp="3qz1zpr7sq9x1">
                  <MessageCircleIcon size={16} className="text-[#D6281F] group-hover:scale-110 transition-transform duration-500" data-id="element-296" data-mp="3ucadd3l3heb" />
                </div>
                <div className="flex flex-col items-center sm:items-start" data-id="element-297" data-mp="2m13elb5sgg8i">
                  <span className="text-white/60 text-[10px] sm:text-xs tracking-wider uppercase block" data-id="element-298" data-mp="2hzi6e3478q4n">
                    Stuur bericht
                  </span>
                  <span className="text-white font-medium tracking-wide text-xs sm:text-base" data-id="element-299" data-mp="39rh7f3w76j45">
                    Contact
                  </span>
                </div>
              </Link>
              <a href="https://maps.google.com/?q=Microfoonstraat+36+1322+BN+Almere" target="_blank" rel="noopener noreferrer" className="group flex flex-col sm:flex-row items-center justify-center sm:gap-3 py-3 sm:py-5 px-2 sm:px-4 hover:bg-white/5 transition-all duration-500" data-id="element-300" data-mp="35o1cqd3ousu3">
                <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#0f0f0f] to-[#1a1a1a] shadow-inner border border-white/5 mb-1 sm:mb-0" data-id="element-301" data-mp="1kbrj6vyroanc">
                  <MapPinIcon size={16} className="text-[#D6281F] group-hover:scale-110 transition-transform duration-500" data-id="element-302" data-mp="1f8gzkae5oyxt" />
                </div>
                <div className="flex flex-col items-center sm:items-start" data-id="element-303" data-mp="1oya9l6ynh5zy">
                  <span className="text-white/60 text-[10px] sm:text-xs tracking-wider uppercase block" data-id="element-304" data-mp="1szvh0csf0s2r">
                    Bezoek ons
                  </span>
                  <span className="text-white font-medium tracking-wide text-xs sm:text-base" data-id="element-305" data-mp="1y001sf0cw8ys">
                    Almere
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
};